import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getAllUnreadMessagesCountRequest } from '../api/chat';
import { UnreadMessagesContextValue } from '../contexts/unreadMessagesContext';

const POLL_INTERVAL_MS = 60 * 1000;

interface Params {
  /** Chat uid whose unread counts are summed ('vyce' for the Legend inbox). */
  userId?: string;
  /** Companies whose chats are counted, e.g. ['vyce', selectedCompany.uuid]. */
  companyIds: string[];
  enabled: boolean;
}

/**
 * Total unread chat messages for the nav badge. The count isn't realtime: it is
 * refetched on route change, when the tab regains focus, every minute while the
 * tab is visible, and on demand via refresh(). Mount once per app, in Main.tsx.
 */
export const useUnreadMessagesCount = ({
  userId,
  companyIds,
  enabled,
}: Params): UnreadMessagesContextValue => {
  const [totalUnreadCount, setTotalUnreadCount] = useState<number>(0);
  const { pathname } = useLocation();
  const companyIdsKey = companyIds.filter(Boolean).join(',');
  const requestKey = enabled && userId && companyIdsKey ? `${userId}|${companyIdsKey}` : '';
  const requestKeyRef = useRef(requestKey);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const refetchQueuedRef = useRef(false);
  requestKeyRef.current = requestKey;

  const fetchCount = useCallback(async () => {
    if (!requestKey || !userId) {
      return;
    }
    // Single-flight: calls made while a request is running collapse into one follow-up.
    if (inFlightRef.current) {
      refetchQueuedRef.current = true;
      return;
    }
    inFlightRef.current = (async () => {
      try {
        const res = await getAllUnreadMessagesCountRequest({ userId, companyIds: companyIdsKey.split(',') });
        // The user or company may have changed while the request was in flight.
        if (requestKeyRef.current === requestKey) {
          setTotalUnreadCount(res?.totalUnreadCount ?? 0);
        }
      } catch (e: any) {
        console.error('Failed to get unread messages count', e?.message);
      } finally {
        inFlightRef.current = null;
        if (refetchQueuedRef.current) {
          refetchQueuedRef.current = false;
          // Latest instance: the queued call may be for a newer user/company.
          fetchCountRef.current();
        }
      }
    })();
  }, [requestKey, userId, companyIdsKey]);
  const fetchCountRef = useRef(fetchCount);
  fetchCountRef.current = fetchCount;

  useEffect(() => {
    if (!requestKey) {
      setTotalUnreadCount(0);
      return;
    }
    fetchCount();
  }, [requestKey, pathname, fetchCount]);

  useEffect(() => {
    if (!requestKey) {
      return;
    }
    const refetchIfVisible = () => {
      if (!document.hidden) {
        fetchCount();
      }
    };
    const intervalId = window.setInterval(refetchIfVisible, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', refetchIfVisible);
    window.addEventListener('focus', refetchIfVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refetchIfVisible);
      window.removeEventListener('focus', refetchIfVisible);
    };
  }, [requestKey, fetchCount]);

  return useMemo(() => ({ totalUnreadCount, refresh: fetchCount }), [totalUnreadCount, fetchCount]);
};
